import fetch from 'cross-fetch'
import { spektrixApiUrl, fetchOptions } from './config'

// Get current customer details
export const getCustomerDetails = async () => {
  const response = await fetch(`${spektrixApiUrl}/customer`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch customer details: ${response.status}`)
  }

  return response.json()
}

// Update customer details
export const updateCustomerDetails = async (updates) => {
  const response = await fetch(`${spektrixApiUrl}/customer`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || `Failed to update customer details: ${response.status}`)
  }

  return response.json()
}

// Change customer password
export const changePassword = async (currentPassword, newPassword) => {
  const response = await fetch(`${spektrixApiUrl}/customer/change-password`, {
    method: 'POST',
    body: JSON.stringify({
      oldPassword: currentPassword,
      newPassword: newPassword
    }),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to change password')
  }

  // Success - response is not JSON, just return true
  return true
}

// Get customer addresses
export const getCustomerAddresses = async () => {
  const response = await fetch(`${spektrixApiUrl}/customer/addresses`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch addresses: ${response.status}`)
  }

  return response.json()
}

// Search addresses by postcode
export const searchAddressesByPostcode = async (postcode) => {
  const response = await fetch(`${spektrixApiUrl}/addresses?postcode=${encodeURIComponent(postcode)}`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to search addresses: ${response.status}`)
  }

  return response.json()
}

// Get full address details by ID
export const getAddressById = async (addressId) => {
  const response = await fetch(`${spektrixApiUrl}/addresses/${addressId}`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch address details: ${response.status}`)
  }

  return response.json()
}

// Add a new address
export const addCustomerAddress = async (address) => {
  const response = await fetch(`${spektrixApiUrl}/customer/addresses`, {
    method: 'POST',
    body: JSON.stringify(address),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to add address')
  }

  return response.json()
}

// Update an existing address
export const updateCustomerAddress = async (addressId, updates) => {
  const response = await fetch(`${spektrixApiUrl}/customer/addresses/${addressId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to update address')
  }

  return response.json()
}

// Delete an address
export const deleteCustomerAddress = async (addressId) => {
  const response = await fetch(`${spektrixApiUrl}/customer/addresses/${addressId}`, {
    method: 'DELETE',
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to delete address')
  }

  return true
}

// Get available statements
export const getStatements = async () => {
  const response = await fetch(`${spektrixApiUrl}/statements`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch statements: ${response.status}`)
  }

  return response.json()
}

// Get customer agreed statements (communication preferences)
export const getAgreedStatements = async () => {
  const response = await fetch(`${spektrixApiUrl}/customer/agreed-statements`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch agreed statements: ${response.status}`)
  }

  return response.json()
}

// Add agreed statement (opt-in)
export const addAgreedStatement = async (statementId) => {
  const response = await fetch(`${spektrixApiUrl}/customer/agreed-statements`, {
    method: 'POST',
    body: JSON.stringify([{ id: statementId }]),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to opt in to statement')
  }

  return response.json()
}

// Remove agreed statement (opt-out)
export const removeAgreedStatement = async (statementId) => {
  const response = await fetch(`${spektrixApiUrl}/customer/agreed-statements/${statementId}`, {
    method: 'DELETE',
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to opt out of statement')
  }

  return true
}

// Validate password reset token
export const validatePasswordToken = async (token) => {
  try {
    const response = await fetch(`${spektrixApiUrl}/customer/validate-password-link?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      ...fetchOptions
    })

    return { valid: response.ok }
  } catch (error) {
    return { valid: false }
  }
}

// Set new password with token
export const setPassword = async (token, newPassword) => {
  const response = await fetch(`${spektrixApiUrl}/customer/set-password`, {
    method: 'POST',
    body: JSON.stringify({
      token: token,
      newPassword: newPassword
    }),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to set password')
  }

  return true
}

// Request password reset
export const requestPasswordReset = async (email, domain) => {
  try {
    await fetch(`${spektrixApiUrl}/customer/forgot-password?emailAddress=${encodeURIComponent(email)}&domain=${encodeURIComponent(domain)}`, {
      method: 'POST',
      ...fetchOptions
    })
    // Always return success to avoid email enumeration
    return true
  } catch (error) {
    // Still return success to avoid revealing if email exists
    return true
  }
}

// Create new customer account
export const createCustomer = async (customerData, domain) => {
  const response = await fetch(`${spektrixApiUrl}/customer?domain=${encodeURIComponent(domain)}`, {
    method: 'POST',
    body: JSON.stringify(customerData),
    ...fetchOptions
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to create account')
  }

  return response.json()
}

// Get customer print-at-home documents
export const getPrintAtHomeDocuments = async () => {
  const response = await fetch(`${spektrixApiUrl}/customer/print-at-home-documents?$expand=2`, {
    method: 'GET',
    ...fetchOptions
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch print-at-home documents: ${response.status}`)
  }

  return response.json()
}

// Download a specific print-at-home document
export const downloadPrintAtHomeDocument = (documentId) => {
  // Open the document in a new window/tab
  // The API returns the document directly (204 status means success but the actual document is returned)
  const url = `${spektrixApiUrl}/print-at-home-documents/${documentId}`
  window.open(url, '_blank')
}